import { Dispatch, useEffect, useRef } from 'react'
import { TerminalEventMonitoring } from '@lightspeed-payments/payments-terminal-sdk'
import { removeFromStorage, trackUserEvent } from '@vend/utilities'
import {
  getPollingTimeout,
  getTransaction,
  isTransactionCompleted,
} from '../../../../../helpers/lspayTransaction'
import { PaymentEvent } from '../use-lspay-payment-server-reducer/types'
import {
  LSPAY_ERROR,
  LSPAY_TRANSACTION_STATUS,
  SWIPER_CANCEL_REASON,
  TRANSACTION_TYPE,
} from '../../../../../helpers/lspayConstants'
import {
  getPaymentDeclinedError,
  isCardSpecificDecline,
} from '../../../../../helpers/errorChecking'
import { WEB_REGISTER } from '../../../../../../../web-register.constant'
import { TAG, trackLSPayServerError } from '../../../../../hooks/errorTracking'
import { SwiperError, TerminalInfo } from '../../../../../helpers/lspay.types'
import { ACTIVE_LSPAY_TRANSACTION } from '../../../../../../../../constants/payments'
import { getTotalSurchargeAmountFromTenders } from '../../../../../../../../functions/payments'
import { sendTerminalEvent } from '../../../../../helpers/terminalMonitoringEventDispatcher'
import { useErrorMessages } from '../../../../../hooks/useErrorMessages'

interface PollTransactionProps {
  paymentIntentId: string
  registerId: string
  registerSalePaymentId: string
  registerSaleId: string
  terminalMonitoringInstance: TerminalEventMonitoring | null
  dispatch: Dispatch<PaymentEvent>
  terminalInfo: TerminalInfo
}
export const usePollTransaction = ({
  registerId,
  paymentIntentId,
  registerSalePaymentId,
  registerSaleId,
  terminalMonitoringInstance,
  dispatch,
  terminalInfo,
}: PollTransactionProps) => {
  const getErrorMessage = useErrorMessages()
  const active = useRef(true)
  const { deviceId, deviceModel } = terminalInfo
  useEffect(() => {
    // Track the event for polling
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'LSD started polling transaction status',
      {
        registerSalePaymentId,
        registerSaleId,
        paymentIntentId,
      }
    )
    const pollTransaction = () => {
      // if it is not active anymore, don't poll
      if (!active.current) {
        return
      }
      getTransaction({
        registerId,
        paymentIntentId,
        registerSalePaymentId,
      })
        .then(data => {
          // if it is not active anymore, don't continue processing
          if (!active.current) {
            return
          }
          if (data.status === LSPAY_TRANSACTION_STATUS.PROCESSING) {
            // keep polling
            timeoutId = setTimeout(pollTransaction, getPollingTimeout())
            return
          }
          // Track the event for polling end
          trackUserEvent(
            WEB_REGISTER.DISPLAY_NAME,
            'LSD ended polling transaction status',
            {
              registerSalePaymentId,
              registerSaleId,
              paymentIntentId,
              status: data.status,
            }
          )

          if (isTransactionCompleted(data)) {
            terminalMonitoringInstance?.sendEvent('COMPLETE', {
              payment: {
                id: paymentIntentId,
                status: 'APPROVED',
              },
              terminal: {
                id: deviceId,
                model: deviceModel,
              },
              transactionType: TRANSACTION_TYPE.PAYMENT,
              ...(getTotalSurchargeAmountFromTenders(data.transaction.tenders)
                ? { surcharging: true }
                : {}),
            })
            dispatch({
              type: 'POLL_RESULT_OK',
              transaction: data.transaction,
            })
            return
          }
          if (data.status === LSPAY_TRANSACTION_STATUS.AUTHORISED) {
            dispatch({
              type: 'AUTHORISED',
              paymentIntentId: data.payment_intent_id,
            })
            return
          }

          if (data.status === LSPAY_TRANSACTION_STATUS.DECLINED) {
            sendTerminalEvent({
              terminalMonitoringInstance,
              intentId: paymentIntentId,
              status: data.status,
              reason: data.reason,
              terminal: {
                id: deviceId,
                model: deviceModel,
              },
              transactionType: TRANSACTION_TYPE.PAYMENT,
            })

            // remove the active lspay transaction from storage for declined transactions
            removeFromStorage(ACTIVE_LSPAY_TRANSACTION)

            dispatch({
              type: 'DECLINED',
              errorMessage: getErrorMessage(
                getPaymentDeclinedError(data.reason),
                isCardSpecificDecline(data.reason)
              ),
              paymentIntentId: data.payment_intent_id,
            })
            return
          }
          if (data.status === LSPAY_TRANSACTION_STATUS.CANCELLED) {
            if (data.reason === SWIPER_CANCEL_REASON.INTENT_CANCELLED) {
              terminalMonitoringInstance?.sendEvent('COMPLETE', {
                payment: {
                  id: paymentIntentId,
                  status: 'ERROR',
                  reason: 'Failed to connect to terminal',
                },
                terminal: {
                  id: deviceId,
                  model: deviceModel,
                },
                transactionType: TRANSACTION_TYPE.PAYMENT,
              })
            } else {
              terminalMonitoringInstance?.sendEvent('COMPLETE', {
                payment: {
                  id: paymentIntentId,
                  status: 'CANCELLED',
                  reason: 'Requested by customer',
                },
                terminal: {
                  id: deviceId,
                  model: deviceModel,
                },
                transactionType: TRANSACTION_TYPE.PAYMENT,
              })
            }

            dispatch({
              type: 'CANCELLED',
              errorMessage: getErrorMessage(LSPAY_ERROR.CANCEL_PAYMENT),
              paymentIntentId: data.payment_intent_id,
              reason: data.reason,
            })
            return
          }
        })
        .catch((error: SwiperError) => {
          // track the error
          trackLSPayServerError(
            '[lspay-sever-driven] failed to get transaction',
            error,
            {
              registerSalePaymentId,
              paymentIntentId,
            },
            TAG.payment
          )

          // keep polling
          timeoutId = setTimeout(pollTransaction, getPollingTimeout())
          // TODO would we ever want to stop polling here ? maybe if the response indicates a bad request
        })
    }

    let timeoutId = setTimeout(pollTransaction, getPollingTimeout())

    return () => {
      clearTimeout(timeoutId)
      active.current = false
    }
  }, [
    paymentIntentId,
    registerId,
    registerSalePaymentId,
    registerSaleId,
    dispatch,
    terminalMonitoringInstance,
    deviceId,
    deviceModel,
    getErrorMessage,
  ])

  // return a function to cancel polling
  return () => {
    active.current = false
  }
}
